import React, { useState, useEffect, useRef } from 'react';
import { ArrowRightLeft, Volume2, ClipboardCopy, Heart } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../context/TranslationContext';
import { speakText } from '../services/translationService';

const TextTranslator: React.FC = () => {
  const {
    sourceLanguage,
    targetLanguage,
    setSourceLanguage,
    setTargetLanguage,
    swapLanguages,
    translateText,
    isTranslating,
    addToPhrasebook
  } = useTranslation();
  
  const [inputText, setInputText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Effect to auto-translate when languages change
  useEffect(() => {
    if (inputText.trim()) {
      handleTranslation();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLanguage, targetLanguage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setInputText(newText);
    
    // Clear previous timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    
    // Set new timeout for translation
    if (newText.trim()) {
      const timeout = setTimeout(() => {
        handleTranslation();
      }, 500);
      setDebounceTimeout(timeout);
    } else {
      setTranslatedText('');
    }
  };

  const handleTranslation = async () => {
    if (!inputText.trim()) return;
    
    try {
      const result = await translateText(inputText);
      setTranslatedText(result.translatedText);
      setIsFavorite(false);
    } catch (error) {
      console.error('Translation failed:', error);
    }
  };

  const handleCopyTranslation = () => {
    if (!translatedText) return;
    
    navigator.clipboard.writeText(translatedText)
      .then(() => {
        // Show toast or feedback
        console.log('Copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
      });
  };
  
  const handleAddToPhrasebook = () => {
    if (!inputText.trim() || !translatedText.trim()) return;
    
    addToPhrasebook({
      sourceText: inputText,
      translatedText,
      sourceLanguage,
      targetLanguage,
      tags: []
    });
    
    setIsFavorite(true);
  };

  const handleSwapLanguages = () => {
    const currentInput = inputText;
    const currentTranslation = translatedText;
    
    swapLanguages();
    setInputText(currentTranslation);
    setTranslatedText(currentInput);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transition-all duration-300">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <LanguageSelector
              id="source-language"
              label="Translate from"
              value={sourceLanguage}
              onChange={setSourceLanguage}
            />
          </div>
          
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={handleSwapLanguages}
              className="inline-flex items-center justify-center p-2 border border-transparent rounded-full text-[#1E90FF] hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[#00A86B] transition-colors duration-200"
              aria-label="Swap languages"
            >
              <ArrowRightLeft className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex-1">
            <LanguageSelector
              id="target-language"
              label="Translate to"
              value={targetLanguage}
              onChange={setTargetLanguage}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <div className="relative">
              <textarea
                ref={inputRef}
                className="w-full h-32 p-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B] focus:border-[#00A86B] resize-none"
                placeholder="Enter text to translate..."
                value={inputText}
                onChange={handleInputChange}
              />
              
              <button
                type="button"
                onClick={() => speakText(inputText, sourceLanguage)}
                className="absolute bottom-2 right-2 p-1.5 text-gray-500 hover:text-[#1E90FF] focus:outline-none"
                aria-label="Listen to source text"
                disabled={!inputText}
              >
                <Volume2 className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="relative bg-gray-50 dark:bg-gray-900 h-32 p-3 border border-gray-300 dark:border-gray-700 rounded-md overflow-auto">
              {isTranslating ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-pulse text-gray-400 dark:text-gray-500">Translating...</div>
                </div>
              ) : (
                <div className="h-full">
                  {translatedText || (
                    <span className="text-gray-400 dark:text-gray-500">Translation will appear here</span>
                  )}
                </div>
              )}
              
              <div className="absolute bottom-2 right-2 flex gap-1">
                <button
                  type="button"
                  onClick={handleAddToPhrasebook}
                  className={`p-1.5 rounded-full focus:outline-none ${
                    isFavorite 
                      ? 'text-red-500 hover:text-red-600' 
                      : 'text-gray-400 hover:text-red-500'
                  }`}
                  aria-label="Add to phrasebook"
                  disabled={!translatedText}
                >
                  <Heart className="h-5 w-5" />
                </button>
                
                <button
                  type="button"
                  onClick={() => speakText(translatedText, targetLanguage)}
                  className="p-1.5 text-gray-500 hover:text-[#1E90FF] focus:outline-none"
                  aria-label="Listen to translation"
                  disabled={!translatedText}
                >
                  <Volume2 className="h-5 w-5" />
                </button>
                
                <button
                  type="button"
                  onClick={handleCopyTranslation}
                  className="p-1.5 text-gray-500 hover:text-[#1E90FF] focus:outline-none"
                  aria-label="Copy translation"
                  disabled={!translatedText}
                >
                  <ClipboardCopy className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextTranslator;